import React, { useState } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import UploadHistoryTable from "./UploadHistoryTable.js";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { Typography, FormControl, InputLabel, Select, MenuItem, TextField } from "@mui/material";
import AppDatePicker from "../common/app-date-picker.jsx";
import AppDropdown from "../common/app-dropdown.jsx";
import TableSearchInput from "./TableSearchInput.js";
import typography from "../cssUtils/typography.js";

const UploadHistory = (props) => {
  const [value, setValue] = useState(null);
  const [datetimeArray, setDateTimeArray] = useState(["year", "month", "day"]);
  const [cleared, setCleared] = useState(false);

  // filter states
  const [fileType, setFileType] = useState("");
  const [status, setStatus] = useState("");
  const [uploadedBy, setUploadedBy] = useState("");
  const [holdingDate, setHoldingDate] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // upload date range dropdown (staged values, committed on Apply)
  const [appliedDateRange, setAppliedDateRange] = useState([null, null]);
  const [dateError, setDateError] = useState(null);

  const handleDateChange = (newDate) => {
    setValue(newDate);
  };

  const handleDateRangeApply = () => {
    setFromDate(appliedDateRange[0]);
    setToDate(appliedDateRange[1]);
  };

  const handleDateRangeCancel = () => {
    setAppliedDateRange([fromDate, toDate]);
    setDateError(null);
  };

  return (
    <div className="App">
      {/* <Header userState={props.userState}/> */}
      <Container maxWidth={false} sx={{ mt: 3 }}>
        {/* <ButtonRow /> */}
        {/* <p>State: {props.userState} Time remaining: {props.remaining}</p> */}
        <Box
          className="upload-date-picker-div"
          sx={{
            display: "flex",
            alignItems: {xs: "flex-start", md: "center"},
            flexDirection: { xs: "column", md: "row" },
            gap: {xs: 1, md: 2},
          }}
        >
          <Typography sx={{ ...typography.h1}} style={{ margin: 0 }}>Latest Upload date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <AppDatePicker
              defaultValue={value}
              onChange={(newValue) => handleDateChange(newValue)}
              disableFuture
              views={datetimeArray}
              slotProps={{
                field: { clearable: true, onClear: () => setCleared(true) },
              }}
              sx={{ width: 260 }}
            />
          </LocalizationProvider>
        </Box>

        {/* Upload Tracker Filters */}
        <Box
          className="upload-filters-div"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            mt: 1,
            mb: 3,
          }}
        >
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 0" },
              minWidth: 200,
              maxWidth: 260,
            }}
          >
            <TableSearchInput
              searchQuery={fileName}
              onSearchChange={(e) => setFileName(e.target.value)}
              placeholder="Search by File Name"
            />
          </Box>

          <FormControl
            size="small"
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 0" },
              minWidth: 140,
              maxWidth: 170,
            }}
          >
            <InputLabel id="file-type-label">File Type</InputLabel>
            <Select
              labelId="file-type-label"
              label="File Type"
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="clients">Clients</MenuItem>
              <MenuItem value="monthly portfolio">Monthly Portfolio</MenuItem>
              <MenuItem value="annual report">Annual Report</MenuItem>
              <MenuItem value="large & midcap">Large & MidCap</MenuItem>
              <MenuItem value="growth plus value">Growth Plus Value</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 0" },
              minWidth: 140,
              maxWidth: 170,
            }}
          >
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Uploaded By"
            size="small"
            value={uploadedBy}
            onChange={(e) => setUploadedBy(e.target.value)}
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 0" },
              minWidth: 140,
              maxWidth: 170,
            }}
          />

          <TextField
            label="File Name"
            size="small"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 0" },
              minWidth: 140,
              maxWidth: 170,
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 0" },
                minWidth: 150,
                maxWidth: 170,
              }}
            >
              <AppDatePicker
                label="Holdings Date"
                value={holdingDate}
                onChange={(newValue) => setHoldingDate(newValue)}
                disableFuture
                slotProps={{
                  field: { clearable: true },
                  textField: { size: "small" },
                }}
                sx={{ width: "100%" }}
              />
            </Box>
          </LocalizationProvider>

          <AppDropdown
            sx={{
              whiteSpace: "nowrap",
              "& .MuiChip-root": {
                maxWidth: fromDate && toDate ? "160px" : "0px",
                transition: "max-width 0.2s ease",
              },
            }}
            menuContent={(closeMenu) => (
              <Box sx={{ p: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="subtitle2">Select Upload Date Range:</Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <DatePicker
                        label="From"
                        value={appliedDateRange[0]}
                        disableFuture
                        onChange={(newValue) => {
                          setAppliedDateRange([newValue, appliedDateRange[1]]);
                          if (appliedDateRange[1] && newValue && dayjs(newValue).isAfter(appliedDateRange[1]))
                            setDateError("From Date must be less than or equal to To Date");
                          else setDateError(null);
                        }}
                        slotProps={{
                          textField: { size: "small", sx: { width: "200px" } },
                        }}
                      />
                      <DatePicker
                        label="To"
                        value={appliedDateRange[1]}
                        disableFuture
                        onChange={(newValue) => {
                          setAppliedDateRange([appliedDateRange[0], newValue]);
                          if (appliedDateRange[0] && newValue && dayjs(newValue).isBefore(appliedDateRange[0]))
                            setDateError("To Date must be greater than or equal to From Date");
                          else setDateError(null);
                        }}
                        slotProps={{
                          textField: { size: "small", sx: { width: "200px" } },
                        }}
                      />
                    </Box>
                    {dateError && (
                      <Typography variant="caption" color="error">
                        {dateError}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button
                        onClick={() => {
                          handleDateRangeCancel();
                          closeMenu();
                        }}
                        variant="outlined"
                      >
                        CANCEL
                      </Button>
                      <Button
                        onClick={() => {
                          handleDateRangeApply();
                          closeMenu();
                        }}
                        variant="contained"
                        disabled={!appliedDateRange[0] || !appliedDateRange[1] || Boolean(dateError)}
                      >
                        APPLY
                      </Button>
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          >
            Upload Date Range
            {fromDate && toDate && (
              <Tooltip
                title={`${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`}
                placement="top"
                arrow
              >
                <Chip
                  label={`${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`}
                  size="small"
                  onDelete={() => {
                    setFromDate(null);
                    setToDate(null);
                    setAppliedDateRange([null, null]);
                  }}
                  sx={{ ml: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                />
              </Tooltip>
            )}
          </AppDropdown>
        </Box>

        <UploadHistoryTable
          key={props.refreshKey}
          refreshKey={props.refreshKey}
          dateInput={value}
          fileType={fileType}
          status={status}
          uploadedBy={uploadedBy}
          holdingDate={holdingDate}
          fileName={fileName}
          fromDate={fromDate}
          toDate={toDate}
        />
      </Container>
    </div>
  );
};
export default UploadHistory;